import type { Tone } from "@/components/ui/pill";
import { formatCount, formatMoney, plural } from "@/lib/format";
import { sourceErrorCode, sourceErrorText } from "./detail/trace-run";
import { experimentTally, finishedChecks, overallTally } from "./scoring/scoring-tally";
import type { Counts, ExperimentStatus, ExperimentSummary } from "./types";

const LIVE: ExperimentStatus[] = ["collecting", "running", "stopping", "aggregating"];
const FINISHED: ExperimentStatus[] = ["completed", "stopped", "failed"];

export function isLive(status: ExperimentStatus | undefined): boolean {
  return status !== undefined && LIVE.includes(status);
}

export function isFinished(status: ExperimentStatus): boolean {
  return FINISHED.includes(status);
}

const LABEL: Record<ExperimentStatus, string> = {
  draft: "Draft",
  collecting: "Collecting",
  running: "Running",
  stopping: "Stopping",
  aggregating: "Finishing",
  completed: "Completed",
  stopped: "Stopped",
  failed: "Failed",
};

export function statusLabel(e: Pick<ExperimentSummary, "status" | "stop_reason">): string {
  if (e.status === "stopped" && e.stop_reason === "cost_limit") return "Stopped at cost limit";
  if (isScoringFailure(e)) return "Scoring failed";
  return LABEL[e.status];
}

/** The run ended failed because scoring did: the worker gave up, or every check failed. */
function isScoringFailure(e: Pick<ExperimentSummary, "status" | "stop_reason">): boolean {
  return (
    e.status === "failed" && (e.stop_reason === "scoring_failed" || e.stop_reason === "all_checks_failed")
  );
}

export function statusTone(status: ExperimentStatus): Tone {
  if (isLive(status)) return "run";
  if (status === "stopped") return "warn";
  if (status === "failed") return "err";
  return "neutral";
}

/** "117 of 120 done · 3 agent errors". A partial result never reads as a full one. */
export function completeness(counts: Counts): string {
  const parts = [`${counts.done} of ${counts.total} done`];
  if (counts.agent_error > 0) parts.push(plural(counts.agent_error, "agent error"));
  if (counts.not_run > 0) parts.push(`${counts.not_run} not run`);
  return parts.join(" · ");
}

/** Items that have reached an end state, for progress bars. */
export function doneCount(counts: Counts): number {
  return counts.done + counts.agent_error + counts.not_run;
}

/** For list bars: a run that scores imported outputs moves with its checks, once the server reports them. */
export function runProgress(e: ExperimentSummary): { value: number; max: number } {
  const tally = e.runner === "dataset_output" ? experimentTally(e) : null;
  if (tally) return { value: finishedChecks(tally), max: tally.total };
  return { value: doneCount(e.counts), max: e.counts.total };
}

/** Why a stopped or failed experiment stopped, in one sentence. */
export function stopMessage(e: ExperimentSummary): string | null {
  // A "Run failed items" moves a failed run back to running; its old stop_reason no longer applies.
  if (!isFinished(e.status)) return null;
  if (e.status === "failed" && e.stop_reason === "source_error")
    return sourceErrorText(sourceErrorCode(e), e.pull?.connection.connector_type);
  if (e.stop_reason === "no_new_traces")
    return e.pull?.continue_from_latest
      ? "No new traces since the last version yet, so nothing was run. Try again later."
      : "No traces matched in this period, so nothing was run.";
  const kept = keptText(e);
  if (e.status === "failed" && e.stop_reason === "all_agent_errors")
    return "The experiment failed: every agent call failed, so nothing was scored. Open an item to see the agent's error.";
  if (e.status === "failed" && e.stop_reason === "scoring_failed") return scoringFailedText(e);
  if (e.status === "failed" && e.stop_reason === "all_checks_failed") return allChecksFailedText(e);
  if (e.status === "failed") return `The experiment failed. ${kept}`;
  if (e.status !== "stopped") return null;
  // Stopped before any item existed (a pull stopped while collecting): nothing ran or was kept.
  if (e.counts.total === 0)
    return e.pull ? "Stopped before any traces were pulled." : "Stopped before anything ran.";
  if (e.stop_reason === "cost_limit")
    return `Stopped at the ${formatMoney(e.spend.limit_usd)} cost limit. ${kept} Scores cover those items only.`;
  if (e.stop_reason === "connector_down") return `Stopped because the agent could not be reached. ${kept}`;
  return `Stopped by hand. ${kept} Scores cover those items only.`;
}

/** "timed", "connector · timed" — which sources a number came from. */
export function sourceLabel(sources: Record<string, number> | undefined): string {
  const used = Object.entries(sources ?? {}).filter(([, n]) => n > 0);
  return used.length ? used.map(([name]) => name).join(" · ") : "unknown";
}

/** Who produced the outputs: the agent's name, or "Recorded outputs" for an import. */
export const sourceName = (e: { agent: { name: string } | null }): string =>
  e.agent?.name ?? "Recorded outputs";

/** A trace run imports every item at once, so what it kept is its scored checks, not its items. */
function keptText(e: ExperimentSummary): string {
  const tally = e.runner === "dataset_output" ? overallTally(e) : null;
  if (tally)
    return `${formatCount(tally.scored)} of ${formatCount(tally.total)} checks were scored and kept.`;
  return `${formatCount(e.counts.done)} of ${formatCount(e.counts.total)} items finished and were kept.`;
}

/** Nothing scored, but some checks may have been skipped, so count the failures only. */
function allChecksFailedText(e: ExperimentSummary): string {
  const tally = overallTally(e);
  const counted = tally ? ` (${formatCount(tally.failed)} failed)` : "";
  return `No check scored${counted}. Check the evaluator's settings or key, then run failed items.`;
}

function scoringFailedText(e: ExperimentSummary): string {
  const kept = experimentTally(e) ? ` ${keptText(e)}` : "";
  return `Scoring stopped: the worker failed three times in a row.${kept}`;
}
